import { useEffect, useMemo, useState, createContext, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { clubInfo } from "@/lib/mbti";
import { whatsappLink } from "@/lib/whatsapp";
import { Avatar } from "@/components/PhotoUpload";
import PlatformAssignModal from "@/components/PlatformAssignModal";
import { useAuth } from "@/context/AuthContext";
import PersonEditDialog from "@/components/PersonEditDialog";
import { ShieldCheck, Briefcase, Phone, Mail, Search, Users, X, MessageCircle, Boxes, Settings as SettingsIcon, Pencil, EyeOff, Eye, CheckSquare, Square } from "lucide-react";

/* Selecção múltipla do admin para ocultar/mostrar pessoas sem passar props por todas as secções. */
const SelectionContext = createContext(null);
const personKind = (person) => (person.role === "consultant" ? "player" : "user");

const TABS = [
  { key: "all", label: "Todos" },
  { key: "coaches", label: "Presidentes e Secretários" },
  { key: "staff", label: "Staff" },
  { key: "consultants", label: "Consultores" },
  { key: "assistants", label: "Assistentes" },
  { key: "teams", label: "Equipas" },
];

/**
 * League directory. Visible to admin / coach / consultant / staff.
 *
 * Features:
 *  - Coaches grouped by club
 *  - Staff grouped by department
 *  - Consultants (all active) grouped by club
 *  - Tab filter (Todos / Coaches / Staff / Consultores)
 *  - Real-time search by name / email / phone / platform
 *  - Platform filter chips (Idealista Pro / Confidencial / Fizbot)
 *  - Admin or staff platforms-manager can assign platforms via modal
 */
export default function Directory() {
  const { user } = useAuth();
  const canManagePlatforms = user?.role === "admin" || (user?.role === "staff" && user?.is_platforms_manager);
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [tab, setTab] = useState(params.get("tab") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const [activePlatformIds, setActivePlatformIds] = useState([]);
  const [modalPlayer, setModalPlayer] = useState(null);
  const [editPerson, setEditPerson] = useState(null);
  const canEditCards = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [hiddenFilter, setHiddenFilter] = useState("all"); // all | visible | hidden
  const [busy, setBusy] = useState(false);

  const selectedList = Object.values(selected);
  const toggleSelect = (person) => {
    const key = `${personKind(person)}:${person.id}`;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { kind: personKind(person), id: person.id, name: person.name };
      return next;
    });
  };
  const applyVisibility = async (hidden) => {
    if (selectedList.length === 0) return;
    setBusy(true);
    try {
      await api.patch("/directory/visibility", {
        items: selectedList.map(({ kind, id }) => ({ kind, id })),
        hidden,
      });
      toast.success(`${selectedList.length} ${hidden ? "ocultado(s) do diretório" : "visível(eis) outra vez"}`);
      setSelected({});
      setSelectMode(false);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const refresh = () => api.get("/directory").then((r) => setData(r.data));
  useEffect(() => { refresh(); }, []);
  // Deep-link from the global search: /directory?q=…&tab=…
  useEffect(() => {
    const qp = params.get("q");
    const tp = params.get("tab");
    if (qp !== null) setQ(qp);
    if (tp) setTab(tp);
  }, [params]);
  useEffect(() => { api.get("/platforms").then((r) => setPlatforms(r.data)).catch(() => {}); }, []);
  const platformById = useMemo(() => Object.fromEntries(platforms.map((p) => [p.id, p])), [platforms]);

  const togglePlatformFilter = (id) => {
    setActivePlatformIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const filtered = useMemo(() => {
    if (!data) return null;
    const needle = q.trim().toLowerCase();
    const matches = (p) => {
      if (hiddenFilter === "hidden" && !p.hidden) return false;
      if (hiddenFilter === "visible" && p.hidden) return false;
      if (activePlatformIds.length && p.role === "consultant") {
        const own = new Set(p.platform_ids || []);
        if (!activePlatformIds.every((pid) => own.has(pid))) return false;
      }
      if (!needle) return true;
      const platformNames = (p.platform_ids || []).map((pid) => platformById[pid]?.name || "").join(" ");
      const hay = `${p.name || ""} ${p.email || ""} ${p.phone || ""} ${p.club || ""} ${p.coach_mbti || ""} ${p.department || ""} ${p.job_title || ""} ${platformNames}`.toLowerCase();
      return hay.includes(needle);
    };
    const filterClubMap = (m) => Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v.filter(matches)]));
    return {
      coaches: filterClubMap(data.coaches || {}),
      staff: (data.staff || []).filter(matches),
      consultants: filterClubMap(data.consultants || {}),
      consultants_no_club: (data.consultants_no_club || []).filter(matches),
      assistants: filterClubMap(data.assistants || {}),
      assistants_no_club: (data.assistants_no_club || []).filter(matches),
    };
  }, [data, q, activePlatformIds, platformById, hiddenFilter]);

  if (!data) return <div className="kw-overline">A carregar diretório…</div>;

  const totalConsultants = Object.values(data.consultants || {}).reduce((a, v) => a + v.length, 0) + (data.consultants_no_club || []).length;
  const totalAssistants = Object.values(data.assistants || {}).reduce((a, v) => a + v.length, 0) + (data.assistants_no_club || []).length;
  const totalResults = filtered ? (
    Object.values(filtered.coaches).reduce((a, v) => a + v.length, 0) +
    filtered.staff.length +
    Object.values(filtered.consultants).reduce((a, v) => a + v.length, 0) +
    filtered.consultants_no_club.length +
    Object.values(filtered.assistants).reduce((a, v) => a + v.length, 0) +
    filtered.assistants_no_club.length
  ) : 0;

  const showCoaches = tab === "all" || tab === "coaches";
  const showStaff = tab === "all" || tab === "staff";
  const showConsultants = tab === "all" || tab === "consultants";
  const showAssistants = tab === "all" || tab === "assistants";
  const showTeams = tab === "teams";

  return (
    <SelectionContext.Provider value={{ selectMode, selected, toggleSelect }}>
    <div className="space-y-6 kw-fade-up" data-testid="directory-page">
      <div>
        <div className="kw-overline">Quem é Quem</div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">
          Diretório <span className="text-[#E50000]">KW Lead</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          A nossa equipa: {totalConsultants} consultores{totalAssistants > 0 && <>, {totalAssistants} assistente{totalAssistants === 1 ? "" : "s"}</>}, coaches dos 4 clubes e staff de apoio. Contacta directamente quem precisares.
        </p>
      </div>

      {/* Sticky filter zone — search + tabs + platform chips remain visible on scroll */}
      <div className="sticky top-14 md:top-12 z-10 -mx-4 md:-mx-10 px-4 md:px-10 py-3 space-y-3 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#1F1F1F]" data-testid="directory-sticky-filters">
        {/* Search + Tabs */}
        <div className="kw-card p-3 flex flex-col md:flex-row gap-3 md:items-center" data-testid="directory-filters">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Procurar por nome, email, telefone, clube, plataforma…"
              className="kw-input !pl-10 !pr-10 w-full"
              data-testid="directory-search" />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white" aria-label="Limpar pesquisa">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-1 flex-wrap">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${tab === t.key ? "bg-[#E50000] text-white" : "border border-[#222] text-[#A3A3A3] hover:text-white hover:border-[#444]"}`}
                data-testid={`directory-tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Platform filter chips */}
        {platforms.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="directory-platform-filters">
            <span className="kw-overline text-[10px] text-[#737373]">Plataformas:</span>
            {platforms.map((p) => {
              const active = activePlatformIds.includes(p.id);
              return (
                <button key={p.id} onClick={() => togglePlatformFilter(p.id)}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-widest font-bold border transition-colors ${active ? "" : "opacity-50 hover:opacity-100"}`}
                  style={{ borderColor: p.color, color: active ? "#fff" : p.color, background: active ? p.color : "transparent" }}
                  data-testid={`directory-platform-chip-${p.id}`}>
                  {p.logo_url ? (
                    <img src={p.logo_url} alt="" className="w-3 h-3 object-contain" />
                  ) : (
                    <Boxes className="w-3 h-3" />
                  )}
                  {p.name}
                </button>
              );
            })}
            {activePlatformIds.length > 0 && (
              <button onClick={() => setActivePlatformIds([])} className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-white" data-testid="directory-platform-clear">
                limpar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Barra de ocultação — só admin */}
      {isAdmin && (
        <div className="kw-card p-3 flex flex-wrap items-center gap-3" data-testid="directory-hide-bar">
          <div className="flex gap-1">
            {[
              { v: "all", label: "Todos" },
              { v: "visible", label: "Visíveis" },
              { v: "hidden", label: "Ocultos" },
            ].map((o) => (
              <button key={o.v} onClick={() => setHiddenFilter(o.v)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${hiddenFilter === o.v ? "bg-[#E50000] text-white" : "border border-[#222] text-[#A3A3A3] hover:text-white hover:border-[#444]"}`}
                data-testid={`directory-hidden-filter-${o.v}`}>
                {o.label}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          {!selectMode ? (
            <button onClick={() => setSelectMode(true)}
              className="kw-btn-secondary !py-1.5 inline-flex items-center gap-2 text-[10px]"
              data-testid="directory-select-mode">
              <CheckSquare className="w-3 h-3" /> Selecionar pessoas
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]" data-testid="directory-selected-count">
                {selectedList.length} seleccionado{selectedList.length === 1 ? "" : "s"}
              </span>
              <button onClick={() => applyVisibility(true)} disabled={busy || selectedList.length === 0}
                className="kw-btn-primary !py-1.5 inline-flex items-center gap-2 text-[10px] disabled:opacity-40"
                data-testid="directory-hide-selected">
                <EyeOff className="w-3 h-3" /> Ocultar seleccionados
              </button>
              <button onClick={() => applyVisibility(false)} disabled={busy || selectedList.length === 0}
                className="kw-btn-secondary !py-1.5 inline-flex items-center gap-2 text-[10px] disabled:opacity-40"
                data-testid="directory-show-selected">
                <Eye className="w-3 h-3" /> Voltar a mostrar
              </button>
              <button onClick={() => { setSelectMode(false); setSelected({}); }}
                className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-white"
                data-testid="directory-select-cancel">
                cancelar
              </button>
            </div>
          )}
        </div>
      )}

      {(q || activePlatformIds.length > 0) && (
        <div className="text-xs text-[#A3A3A3]" data-testid="directory-results-count">
          {totalResults} resultado{totalResults === 1 ? "" : "s"}{q && <> para &quot;<span className="text-white">{q}</span>&quot;</>}
        </div>
      )}

      {showCoaches && <CoachesSection coaches={filtered.coaches} president={data?.league_president || []} />}
      {showStaff && filtered.staff.length > 0 && <StaffSection staff={filtered.staff} />}
      {showConsultants && (
        <ConsultantsSection
          consultants={filtered.consultants}
          noClub={filtered.consultants_no_club}
          platformById={platformById}
          canManagePlatforms={canManagePlatforms}
          onManagePlatforms={(p) => setModalPlayer(p)}
          onEdit={canEditCards ? setEditPerson : null}
        />
      )}
      {showAssistants && (
        <AssistantsSection
          assistants={filtered.assistants}
          noClub={filtered.assistants_no_club}
          onEdit={canEditCards ? setEditPerson : null}
        />
      )}
      {showTeams && (
        <TeamsSection
          filtered={filtered}
          platformById={platformById}
          canManagePlatforms={canManagePlatforms}
          onManagePlatforms={(p) => setModalPlayer(p)}
          onEdit={canEditCards ? setEditPerson : null}
        />
      )}

      {editPerson && (
        <PersonEditDialog person={editPerson} onClose={() => setEditPerson(null)} onSaved={refresh} />
      )}

      {modalPlayer && (
        <PlatformAssignModal
          player={modalPlayer}
          platforms={platforms}
          onClose={() => setModalPlayer(null)}
          onSaved={() => refresh()}
        />
      )}
    </div>
    </SelectionContext.Provider>
  );
}

function CoachesSection({ coaches, president }) {
  const clubs = Object.keys(coaches);
  return (
    <section data-testid="directory-coaches">
      <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
        <ShieldCheck className="w-5 h-5 text-[#E50000]" />
        <h2 className="font-display text-2xl font-black uppercase">Presidentes e Secretários</h2>
      </div>
      {president.length > 0 && (
        <div className="kw-card p-4 mb-4 max-w-md border-[#E50000]" data-testid="directory-league-president">
          <div className="kw-overline mb-3 text-[#E50000]">Presidente da Liga</div>
          <div className="space-y-3">{president.map((c) => <PersonCard key={c.id} person={c} />)}</div>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {clubs.map((club) => {
          const info = clubInfo(club);
          return (
            <div key={club} className="kw-card p-4" style={{ borderColor: info?.border }} data-testid={`directory-club-${club}`}>
              <div className="kw-overline mb-3" style={{ color: info?.color }}>{club}</div>
              {coaches[club].length === 0 ? (
                <p className="text-xs text-[#737373] italic">Sem resultados</p>
              ) : (
                <div className="space-y-3">
                  {coaches[club].map((c) => <PersonCard key={c.id} person={c} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function StaffSection({ staff }) {
  return (
    <section data-testid="directory-staff">
      <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
        <Briefcase className="w-5 h-5 text-[#EAB308]" />
        <h2 className="font-display text-2xl font-black uppercase">Staff KW Lead</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {staff.map((s) => <PersonCard key={s.id} person={s} showDept />)}
      </div>
    </section>
  );
}

function ConsultantsSection({ consultants, noClub, platformById, canManagePlatforms, onManagePlatforms, onEdit }) {
  const clubs = Object.keys(consultants);
  const totalShown = clubs.reduce((a, c) => a + consultants[c].length, 0) + noClub.length;
  if (totalShown === 0) return null;
  return (
    <section data-testid="directory-consultants">
      <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
        <Users className="w-5 h-5 text-[#3B82F6]" />
        <h2 className="font-display text-2xl font-black uppercase">Consultores ({totalShown})</h2>
      </div>
      <div className="space-y-5">
        {clubs.map((club) => consultants[club].length > 0 && (
          <div key={club} data-testid={`directory-consultants-${club}`}>
            <div className="kw-overline mb-3" style={{ color: clubInfo(club)?.color || "#3B82F6" }}>{club} · {consultants[club].length}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {consultants[club].map((c) => (
                <PersonCard key={c.id} person={c} compact
                  platformById={platformById}
                  canManagePlatforms={canManagePlatforms}
                  onManagePlatforms={onManagePlatforms}
                  onEdit={onEdit} />
              ))}
            </div>
          </div>
        ))}
        {noClub.length > 0 && (
          <div data-testid="directory-consultants-noclub">
            <div className="kw-overline mb-3 text-[#737373]">Sem clube atribuído · {noClub.length}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {noClub.map((c) => (
                <PersonCard key={c.id} person={c} compact
                  platformById={platformById}
                  canManagePlatforms={canManagePlatforms}
                  onManagePlatforms={onManagePlatforms}
                  onEdit={onEdit} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function AssistantsSection({ assistants, noClub, onEdit }) {
  const clubs = Object.keys(assistants);
  const totalShown = clubs.reduce((a, c) => a + assistants[c].length, 0) + noClub.length;
  if (totalShown === 0) return null;
  return (
    <section data-testid="directory-assistants">
      <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
        <Users className="w-5 h-5 text-[#FFB020]" />
        <h2 className="font-display text-2xl font-black uppercase">Assistentes ({totalShown})</h2>
      </div>
      <div className="space-y-5">
        {clubs.map((club) => assistants[club].length > 0 && (
          <div key={club} data-testid={`directory-assistants-${club}`}>
            <div className="kw-overline mb-3" style={{ color: clubInfo(club)?.color || "#FFB020" }}>{club} · {assistants[club].length}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {assistants[club].map((c) => <PersonCard key={c.id} person={c} compact onEdit={onEdit} />)}
            </div>
          </div>
        ))}
        {noClub.length > 0 && (
          <div data-testid="directory-assistants-noclub">
            <div className="kw-overline mb-3 text-[#737373]">Sem clube atribuído · {noClub.length}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {noClub.map((c) => <PersonCard key={c.id} person={c} compact onEdit={onEdit} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function TeamsSection({ filtered, platformById, canManagePlatforms, onManagePlatforms, onEdit }) {
  // Flatten all consultants + assistants (with or without club) into one list.
  const allPeople = [
    ...Object.values(filtered.consultants).flat(),
    ...filtered.consultants_no_club,
    ...Object.values(filtered.assistants).flat(),
    ...filtered.assistants_no_club,
  ];
  // Group by team_id — keep track of a printable name.
  const byTeam = new Map();
  const orphans = [];
  for (const p of allPeople) {
    if (p.team_id && p.team_name) {
      if (!byTeam.has(p.team_id)) byTeam.set(p.team_id, { id: p.team_id, name: p.team_name, members: [] });
      byTeam.get(p.team_id).members.push(p);
    } else {
      orphans.push(p);
    }
  }
  const teams = Array.from(byTeam.values()).sort((a, b) => a.name.localeCompare(b.name));

  if (teams.length === 0 && orphans.length === 0) return null;

  const renderPerson = (p) => (
    <PersonCard key={p.id} person={p} compact
      platformById={platformById}
      canManagePlatforms={canManagePlatforms}
      onManagePlatforms={onManagePlatforms}
      onEdit={onEdit} />
  );

  return (
    <section data-testid="directory-teams">
      <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
        <Users className="w-5 h-5 text-[#FFB020]" />
        <h2 className="font-display text-2xl font-black uppercase">Equipas ({teams.length})</h2>
      </div>
      <div className="space-y-6">
        {teams.map((t) => {
          const lead = t.members.find((m) => m.is_team_lead);
          const consultants = t.members.filter((m) => m.role_in_team !== "assistant" && !m.is_team_lead);
          const assistants = t.members.filter((m) => m.role_in_team === "assistant");
          return (
            <div key={t.id} className="kw-card p-5 border-[#FFB020]/30" data-testid={`directory-team-${t.id}`}>
              <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-[#FFB020]/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FFB020]/10 border border-[#FFB020]/40 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#FFB020]" />
                  </div>
                  <div>
                    <div className="kw-overline text-[10px] text-[#FFB020]">Equipa KW</div>
                    <div className="font-display text-xl font-black uppercase tracking-tight">{t.name}</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                  {t.members.length} membro{t.members.length === 1 ? "" : "s"}
                </div>
              </div>
              {lead && (
                <div className="mb-4">
                  <div className="kw-overline mb-2 text-[10px] text-[#E50000]">Team Lead</div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">{renderPerson(lead)}</div>
                </div>
              )}
              {consultants.length > 0 && (
                <div className="mb-4">
                  <div className="kw-overline mb-2 text-[10px] text-[#3B82F6]">Consultores · {consultants.length}</div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">{consultants.map(renderPerson)}</div>
                </div>
              )}
              {assistants.length > 0 && (
                <div>
                  <div className="kw-overline mb-2 text-[10px] text-[#FFB020]">Assistentes · {assistants.length}</div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">{assistants.map(renderPerson)}</div>
                </div>
              )}
            </div>
          );
        })}
        {orphans.length > 0 && (
          <div className="kw-card p-5 border-[#333]" data-testid="directory-team-orphans">
            <div className="kw-overline mb-3 text-[#737373]">Sem equipa atribuída · {orphans.length}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">{orphans.map(renderPerson)}</div>
          </div>
        )}
      </div>
    </section>
  );
}

function PlatformBadges({ ids, platformById }) {
  if (!ids || ids.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1" data-testid="person-platforms">
      {ids.map((id) => {
        const p = platformById[id];
        if (!p) return null;
        return (
          <span key={id} title={p.name}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold border"
            style={{ borderColor: p.color, color: p.color, background: `${p.color}10` }}
            data-testid={`person-platform-${id}`}>
            {p.logo_url ? (
              <img src={p.logo_url} alt="" className="w-3 h-3 object-contain" />
            ) : (
              <Boxes className="w-2.5 h-2.5" />
            )}
            <span className="hidden sm:inline">{p.name}</span>
          </span>
        );
      })}
    </div>
  );
}

function PersonCard({ person, showDept, compact, platformById, canManagePlatforms, onManagePlatforms, onEdit }) {
  const avatarSize = compact ? 40 : 56;
  const club = person.club ? clubInfo(person.club) : null;
  const stop = (e) => e.stopPropagation();
  const sel = useContext(SelectionContext);
  const selectMode = sel?.selectMode;
  const isSelected = !!sel?.selected?.[`${personKind(person)}:${person.id}`];
  const handleClick = selectMode ? () => sel.toggleSelect(person) : (onEdit ? () => onEdit(person) : undefined);
  return (
    <div
      onClick={handleClick}
      title={selectMode ? "Clica para seleccionar" : (onEdit ? "Clica para editar o cartão" : undefined)}
      className={`border p-3 transition-colors flex gap-3 relative ${isSelected ? "border-[#E50000] bg-[#E50000]/5" : "border-[#222]"} ${person.hidden ? "opacity-60" : ""} ${selectMode ? "[&_a]:pointer-events-none [&_button]:pointer-events-none" : ""} ${(onEdit || selectMode) ? "cursor-pointer hover:border-[#E50000]" : "hover:border-[#444]"}`}
      data-testid={`directory-person-${person.id}`}>
      {selectMode && (
        <span className="absolute top-2 right-2 text-[#E50000]" data-testid={`select-box-${person.id}`}>
          {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-[#444]" />}
        </span>
      )}
      {!selectMode && onEdit && (
        <span className="absolute top-2 right-2 text-[#444]" data-testid={`edit-hint-${person.id}`}>
          <Pencil className="w-3 h-3" />
        </span>
      )}
      <div className="flex-shrink-0">
        <Avatar photoUrl={person.photo_url} name={person.name} size={avatarSize} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-display font-black uppercase tracking-tight truncate ${compact ? "text-sm" : ""}`}>{person.name}</div>
        {person.hidden && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#737373] text-[#737373]"
            title="Oculto no diretório, na pesquisa e nas páginas públicas — só admin e staff o vêem"
            data-testid={`person-hidden-${person.id}`}>
            <EyeOff className="w-2.5 h-2.5" /> Oculto
          </span>
        )}
        {showDept && person.department && (
          <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mt-1">{person.department}</div>
        )}
        {showDept && person.job_title && (
          <div className="text-[11px] text-[#A3A3A3] mt-0.5" data-testid={`person-job-title-${person.id}`}>{person.job_title}</div>
        )}
        {person.coach_mbti && club && (
          <div
            className="mt-1 inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border"
            style={{ background: club.bg, borderColor: club.border, color: club.color }}
          >
            <span className="font-mono">{person.coach_mbti}</span>
            {person.coach_personality_label && <> · {person.coach_personality_label}</>}
          </div>
        )}
        {/* Role tags: Team Lead (prioridade) ou Assistente */}
        {person.club_role === "president" && (
          <span className="ml-1 mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#EAB308] text-[#EAB308]"
            title="Consultor Presidente da Liga — entidade máxima da Liga, não coopete">
            👑 Presidente da Liga
          </span>
        )}
        {person.club_role === "coach" && (
          <span className="ml-1 mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#EAB308] text-[#EAB308]"
            title="Secretário do Clube — escolhido pelo plantel, entra no Ranking dos Secretários">
            🤝 Secretário do Clube
          </span>
        )}
        {person.club_role === "administrator" && (
          <span className="ml-1 mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#EAB308] text-[#EAB308]"
            title="Presidente do Clube — zela pela união e administração do clube, não coopete">
            ⚖️ Presidente do Clube
          </span>
        )}
        {person.is_team_lead && (
          <span className="ml-1 mt-1 inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#E50000] text-[#E50000]"
            title="Lidera a equipa">
            Team Lead
          </span>
        )}
        {person.role_in_team === "assistant" && (
          <span className="ml-1 mt-1 inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#FFB020] text-[#FFB020]"
            title="Assistente — campeonato separado">
            Assistente
          </span>
        )}
        {person.team_name && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#FFB020]/40 text-[#FFB020] bg-[#FFB020]/5"
            title={`Equipa: ${person.team_name}`}
            data-testid={`person-team-${person.id}`}>
            <Users className="w-2.5 h-2.5" /> {person.team_name}
          </div>
        )}
        {person.role === "consultant" && platformById && (
          <PlatformBadges ids={person.platform_ids} platformById={platformById} />
        )}
        <div className="mt-2 space-y-1 text-xs">
          {person.email && (
            <a onClick={stop} href={`mailto:${person.email}`} className="flex items-center gap-2 text-[#A3A3A3] hover:text-white truncate">
              <Mail className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{person.email}</span>
            </a>
          )}
          {person.phone && (
            <div className="flex items-center gap-2">
              <a onClick={stop} href={`tel:${person.phone.replace(/\s+/g, "")}`} className="flex items-center gap-2 text-[#A3A3A3] hover:text-white">
                <Phone className="w-3 h-3 flex-shrink-0" /> {person.phone}
              </a>
              {whatsappLink(person.phone) && (
                <a
                  onClick={stop}
                  href={whatsappLink(person.phone)}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#25D366] hover:text-white px-1.5 py-0.5 border border-[#25D366]/40 hover:bg-[#25D366]/20 transition-colors"
                  title={`Abrir conversa no WhatsApp com ${person.name}`}
                  data-testid={`whatsapp-${person.id}`}>
                  <MessageCircle className="w-3 h-3" /> WhatsApp
                </a>
              )}
            </div>
          )}
        </div>
        {person.role === "consultant" && canManagePlatforms && onManagePlatforms && (
          <button onClick={(e) => { stop(e); onManagePlatforms(person); }}
            className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#737373] hover:text-white"
            data-testid={`manage-platforms-${person.id}`}>
            <SettingsIcon className="w-3 h-3" /> Plataformas
          </button>
        )}
      </div>
    </div>
  );
}
